import React from "react";
import { Dialog, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import CloseIcon from "@mui/icons-material/Close";
import InstaColors, { fontFamily } from "../../Constants/constants";
import "./ParallelProcessModal.css";

interface UploadProgress {
  fileName: string;
  status: "pending" | "processing" | "completed" | "failed";
  message: string;
}

interface ParallelProcessModalProps {
  open: boolean;
  uploadProgress: Record<string, UploadProgress>;
  title?: string;
  uploadingMessage?: string;
  processingMessage?: string;
  onCancel?: () => void;
}

export const ParallelProcessModal: React.FC<ParallelProcessModalProps> = ({
  open,
  uploadProgress,
  title = "File Upload in Progress",
  uploadingMessage = "files simultaneously...",
  processingMessage = "Files are being processed in parallel for faster upload",
  onCancel,
}) => {
  const uploadCount = Object.keys(uploadProgress).length;
  const isSingleUpload = uploadCount === 1;

  const handleCancel = () => {
    const userConfirmed = window.confirm(
      isSingleUpload
        ? "Are you sure you want to cancel uploading this file?"
        : "Are you sure you want to cancel uploading these files?"
    );
    if (userConfirmed) {
      if (onCancel) {
        onCancel();
      } else {
        window.location.reload();
      }
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 10,
          padding: 20,
        },
      }}
    >
      <div className="parallel-modal-container">
        <div className="parallel-modal-header">
          <div className="parallel-modal-title-section">
            <CircularProgress size={24} className="parallel-modal-spinner" />
            <h6 className="parallel-modal-title" style={{ fontFamily }}>
              {title}
            </h6>
          </div>
          <div className="parallel-modal-close-button" onClick={handleCancel}>
            <CloseIcon
              className="parallel-modal-close-icon"
              style={{ color: InstaColors.primary }}
            />
          </div>
        </div>

        <div className="parallel-modal-progress-list">
          {Object.entries(uploadProgress).map(([index, progress]) => (
            <div key={index} className="parallel-modal-progress-item">
              <div className={`parallel-modal-status-indicator ${progress.status}`}>
                {progress.status === "completed" && (
                  <CheckCircleIcon className="parallel-modal-status-icon" />
                )}
                {progress.status === "failed" && (
                  <DoDisturbIcon className="parallel-modal-status-icon" />
                )}
                {progress.status === "processing" && (
                  <CircularProgress size={12} className="parallel-modal-status-spinner" />
                )}
              </div>

              <div className="parallel-modal-file-info">
                <div className="parallel-modal-file-name" style={{ fontFamily }}>
                  {progress.fileName}
                </div>
                <div className="parallel-modal-file-message" style={{ fontFamily }}>
                  {progress.message}
                </div>
              </div>

              <div
                className={`parallel-modal-status-badge ${progress.status}`}
                style={{ fontFamily }}
              >
                {progress.status.toUpperCase()}
              </div>
            </div>
          ))}
        </div>

        <div className="parallel-modal-footer">
          <div className="parallel-modal-upload-count" style={{ fontFamily }}>
            {isSingleUpload
              ? "Uploading 1 file..."
              : `Uploading ${uploadCount} ${uploadingMessage}`}
          </div>
          {!isSingleUpload && (
            <div className="parallel-modal-processing-info" style={{ fontFamily }}>
              {processingMessage}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
